// ============================================
// Analytics Services — Image Votes + Game Data
// Extracted from main.js
// ============================================

import { events, Events } from '../engine/events.js';
import { isPat } from '../engine/pgate.js';
import { variableManager } from '../engine/variables.js';
import { achievementManager } from '../systems/achievements.js';
import { statisticsTracker } from '../systems/statistics.js';

// VERSION_INFO is imported lazily via the getter to avoid circular dependency
let _getVersionInfo = null;
export function setVersionInfoGetter(fn) { _getVersionInfo = fn; }
function _versionInfo() { return _getVersionInfo ? _getVersionInfo() : { version: '?', fullVersion: '?' }; }

// ============================================
// Privacy helpers — explicit consent + daily-rotating session id
// ============================================
// GDPR strict reading: analytics need explicit opt-in. Nothing gets sent
// until the user actively chooses "Yes" in the first-run consent modal
// (or flips the toggle in Settings). No persistent cross-day identifier is
// stored, so a player can't be tracked from one day to the next even when
// consent is granted. Keys in localStorage:
//   PnO_AnalyticsConsent      '1' = opted in, '0' = opted out, missing = no choice yet
//   PnO_AnalyticsSid_YYYYMMDD  today's random 6-hex, auto-rolls at midnight
// We also retire the old persistent PnO_PlayerId on init.
export function hasAnalyticsConsent() {
    try { return localStorage.getItem('PnO_AnalyticsConsent') === '1'; } catch(e) { return false; }
}
export function hasAnalyticsChoice() {
    try { return localStorage.getItem('PnO_AnalyticsConsent') !== null; } catch(e) { return false; }
}
// Back-compat alias — used by guards throughout; inverts hasAnalyticsConsent.
export function isAnalyticsOptedOut() { return !hasAnalyticsConsent(); }

function _todayStamp() {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
}

/** Returns an anonymous YYYYMMDD-<6hex> session id, fresh each calendar day.
 *  Returns empty string when the user has opted out (or not consented yet) —
 *  callers that accidentally build a payload without a prior consent check
 *  then send garbage in the id field but don't pollute localStorage. */
export function dailySessionId() {
    if (!hasAnalyticsConsent()) return '';
    const stamp = _todayStamp();
    const key = `PnO_AnalyticsSid_${stamp}`;
    let hex;
    try {
        hex = localStorage.getItem(key);
        if (!hex) {
            const bytes = new Uint8Array(3);
            (crypto.getRandomValues?.(bytes));
            hex = Array.from(bytes).map(b => b.toString(16).padStart(2,'0')).join('');
            if (!/^[0-9a-f]{6}$/.test(hex)) {
                hex = Math.random().toString(16).slice(2, 8);
            }
            localStorage.setItem(key, hex);
            // Drop yesterday's (and older) sid keys so the device stores at most one rotating session id.
            // Collect first, then remove — removing inside a forward key(i) loop
            // shifts indices and skips entries.
            const toRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && (k.startsWith('PnO_AnalyticsSid_') || k.startsWith('PnO_AnalyticsId_')) && k !== key) {
                    toRemove.push(k);
                }
            }
            for (const k of toRemove) {
                try { localStorage.removeItem(k); } catch(e) {}
            }
            // Retire the old persistent PnO_PlayerId from pre-0.2.4.
            try { localStorage.removeItem('PnO_PlayerId'); } catch(e) {}
        }
    } catch(e) {
        hex = Math.random().toString(16).slice(2, 8);
    }
    return `${stamp}-${hex}`;
}

// ============================================
// Image Vote Service (batched VPS sends)
// ============================================
export const imageVoteService = (() => {
    const VPS_URL = 'https://www.pinkedandowned.com/api/analytics/votes';
    const FLUSH_INTERVAL = 180000; // 3 minutes
    const MAX_BATCH = 10;
    let pending = [];
    let flushTimer = null;

    function init() {
        flushTimer = setInterval(flush, FLUSH_INTERVAL);
        window.addEventListener('beforeunload', flushSync);
    }

    function isEnabled() {
        // Image voting has its own explicit opt-in, AND must respect the
        // global analytics opt-out.
        if (isAnalyticsOptedOut()) return false;
        try { return localStorage.getItem('ImageVoteEnabled') === '1'; }
        catch(e) { return false; }
    }

    function recordVote(imageName, isPositive) {
        if (!isEnabled()) return;
        pending.push({
            name: imageName,
            up: isPositive,
            version: _versionInfo().fullVersion
        });
        if (pending.length >= MAX_BATCH) flush();
    }

    function _buildPayload(batch) {
        return JSON.stringify({
            votes: batch.map(v => ({ image: v.name, vote: v.up ? 1 : -1 })),
            version: batch[0]?.version || ''
        });
    }

    function flush() {
        if (pending.length === 0) return;
        const batch = pending.splice(0, MAX_BATCH);
        fetch(VPS_URL, { method: 'POST', headers: {'Content-Type': 'application/json'}, body: _buildPayload(batch) }).catch(() => {});
    }

    function flushSync() {
        if (pending.length === 0) return;
        const batch = pending.splice(0, MAX_BATCH);
        try { navigator.sendBeacon(VPS_URL, new Blob([_buildPayload(batch)], {type: 'application/json'})); } catch(e) {}
    }

    return { init, recordVote, flush, isEnabled };
})();

// ============================================
// Game Data Service (VPS Session Analytics)
// ============================================
export const gameDataService = (() => {
    const VPS_URL = 'https://www.pinkedandowned.com/api/analytics/session';
    const MIN_SESSION_SEC = 180;
    const SEND_SCHEDULE = [180, 600]; // 3min first, then 10min

    let sessionStart = 0, sendIndex = 0;
    let isSending = false, storyReady = false, sentFinal = false;
    let sendTimer = null;
    let lastLocation = '';
    const locationVisits = {};

    function init() {
        // Respect opt-out: don't wire any handlers, don't generate an id.
        if (isAnalyticsOptedOut()) return;

        // Listen for story creation
        events.on(Events.SCREEN_CHANGE, (screen) => {
            if (screen === 'screen-game-view') onNewStory();
        });
        // Track location visits. currentLocation is a JS property on the
        // story manager, NOT an Ink var, so it never fired a VARIABLE_CHANGED
        // event (locationVisits stayed empty). Read it off window._sm at each
        // fresh choice list (one per location/hub entry) and count distinct
        // consecutive entries.
        events.on(Events.STORY_CHOICES_READY, () => {
            try {
                const loc = window._sm?.currentLocation;
                if (loc && loc !== lastLocation) {
                    lastLocation = loc;
                    locationVisits[loc] = (locationVisits[loc] || 0) + 1;
                }
            } catch(e) {}
        });
        // Visibility change (tab hidden = pause)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) trySend();
        });
        // beforeunload beacon
        window.addEventListener('beforeunload', sendBeacon);
    }

    function onNewStory() {
        sessionStart = Date.now();
        storyReady = true;
        sentFinal = false;
        sendIndex = 0;
        lastLocation = '';
        Object.keys(locationVisits).forEach(k => delete locationVisits[k]);
        clearTimeout(sendTimer);
        scheduleNext();
    }

    function scheduleNext() {
        const delay = sendIndex < SEND_SCHEDULE.length ? SEND_SCHEDULE[sendIndex] : 600;
        sendTimer = setTimeout(() => { sendIndex++; trySend(); scheduleNext(); }, delay * 1000);
    }

    function _buildVpsPayload() {
        const day = gv('Day');
        if (day < 1) return null;
        const sessionMin = Math.max(1, Math.floor((Date.now() - sessionStart) / 60000));
        const isPatBuild = isPat();
        const res = `${window.innerWidth}x${window.innerHeight}`;

        // Activities
        let activities = null;
        if (typeof statisticsTracker !== 'undefined' && statisticsTracker) {
            const st2 = statisticsTracker;
            activities = {
                blowjobs: st2.BlowjobsGiven||0, anal: st2.AnalSex||0, hookups: st2.Hookups||0,
                stripped: st2.TimesStripped||0, edged: st2.TimesEdged||0, glory_hole: st2.GloryHoleVisits||0,
                money_earned: st2.TotalMoneyEarned||0, money_spent: st2.TotalMoneySpent||0
            };
        }

        // Dating
        const dating = {};
        ['tyler','kai','owen','raven','sasha','eloise','scar','patrick','vincent'].forEach(n => {
            dating[n] = { stage: gv('dating_'+n), matched: gb('matched_'+n) };
        });

        // Achievements
        let achCount = 0;
        try { achCount = achievementManager?.getUnlockedCount?.() || 0; } catch(e) {}

        return JSON.stringify({
            // Daily-rotating anonymous session id. No persistent cross-day
            // identifier — same device on two different days = two different ids.
            session_id: dailySessionId(),
            version: _versionInfo().version,
            channel: isPatBuild ? 'patreon' : 'free',
            platform: window.Capacitor ? 'Android' : window.electronAPI ? 'Electron' : 'Web',
            duration_sec: sessionMin * 60,
            day: day,
            time_slot: ['Morn','Lunch','Eve','Night'][gv('TimeSlot')] || '?',
            data: {
                // Stats
                fem: gv('Femininity'), sub: gv('Submissiveness'), arousal: gv('Arousal'),
                shame: gv('Shame'), energy: gv('Energy'), max_energy: gv('max_energy'),
                money: gv('Money'), master_rel: gv('MasterRelationship'), fame: gv('sissy_fame'),
                difficulty: gv('difficulty_score'), resolution: res,
                // Tiers
                fem_tier: gv('fem_tier'), sub_tier: gv('sub_tier'),
                allure: gv('Allure'), comfort: gv('Comfort'),
                // Flags
                flags: {
                    chastity: gb('wearing_chastity'), plug: gb('wearing_plug'), long_hair: gb('has_long_hair'),
                    hairless: gb('body_hairless'), club: gb('club_access'), girlfriend: !gb('girlfriend_broken_up'),
                    hrt: gb('taking_hormones'), fem_equipped: gb('EquippedFeminine'), dressed: gb('is_dressed'),
                    loft: gb('moved_out'), cheat: gb('debug_console_installed'), vib: gb('wearing_vib'),
                    spa: gb('spa_member'), dare: gb('dareme_installed')
                },
                // Sets
                sets: {
                    lingerie: gb('set_full_lingerie'), maid: gb('set_maid_service'), neko: gb('set_neko'),
                    office: gb('set_office_ready'), bunny: gb('set_bunny_girl'), cozy: gb('set_cozy_femme'),
                    dark_doll: gb('set_dark_doll')
                },
                // Character
                background: gv('player_background'), penis_size: gv('penis_size'),
                trait_1: gs('trait_1'), trait_2: gs('trait_2'),
                // Upgrades
                upgrades: { bed: gv('upgrade_bed'), desk: gv('upgrade_desk'), vanity: gv('upgrade_vanity'), decor: gv('upgrade_decor') },
                rent: { amount: gv('RentAmount'), due_day: gv('RentDueDay'), missed: gv('RentMissedCount') },
                // Storylines
                master: {
                    orders: { chastity: gv('master_order_chastity'), plug: gv('master_order_plug'), laser: gv('master_order_laser'), makeup: gv('master_order_makeup') },
                    reward: gb('master_reward_given'), opened_up: gb('master_opened_up')
                },
                office: { stage: gv('office_stage'), boss_stage: gv('office_boss_stage'), shifts: gv('office_shifts'), boss_rel: gv('boss_relationship') },
                girlfriend: { rel: gv('sarah_relationship'), suspicion: gv('girlfriend_suspicion'), accepts: gb('girlfriend_accepts_fem'), postconfession: gv('girlfriend_postconfession_dates'), cucked: gb('girlfriend_cucked'), serves_master: gv('girlfriend_serves_master') },
                sugar: { arrangement: gv('sugar_arrangement'), dates: gv('sugar_dates'), matched: gb('matched_richard') },
                hypno: { exposure: gv('hypno_exposure'), master_session: gb('hypno_master_session'), obedience: gv('obedience_trained') },
                // Activities
                activities: activities,
                // Dating
                dating: dating,
                // Streaming
                streaming: {
                    followers: gv('sissy_followers'), cute: gv('streams_cute'), naughty: gv('streams_naughty'), domme: gv('streams_domme'),
                    passive_income: gv('stream_passive_income'),
                    collabs: { lily: gv('collab_lily_done'), master: gv('collab_master_done'), pink: gv('collab_pink_done') },
                    milestones: [1,2,3,4,5].map(i => gb(`stream_milestone_${i}`))
                },
                // Spa
                spa: { member: gb('spa_member'), discovered: gb('spa_discovered'), visits: gv('spa_visits'), masseur: gv('masseur_stage'), camille: gv('camille_stage') },
                // Dare
                dare: { completed: gv('dares_completed'), active: gv('dare_active') },
                // Vibrator
                vib: { wearing: gb('wearing_vib'), activated: gb('vib_activated'), count: gv('vib_activation_count') },
                // Bar
                bar_outfit: gv('bar_outfit'),
                // Endgame
                endgame: {
                    dreams: [1,2,3].map(i => ({ seen: gb(`endgame_dream_${i}`), intimate: gb(`endgame_dream_${i}i`), final: gb(`endgame_dream_${i}f`) })),
                    triggered: gb('endgame_triggered')
                },
                // Milestones
                fem_milestones: [10,25,50,75,100].map(v => gb(`milestone_fem_${v}`)),
                sub_milestones: [10,25,50,75,100].map(v => gb(`milestone_sub_${v}`)),
                shame_thresholds: { s50: gb('shame_50_seen'), s75: gb('shame_75_seen'), s90: gb('shame_90_seen') },
                // Status
                status: { dirty: gb('is_dirty'), drunk: gb('is_drunk'), tired: gb('is_tired'), makeup: gb('has_makeup_on'), pro_makeup: gb('pro_makeup'), aroused: gb('is_visibly_aroused') },
                // Achievements
                achievements: achCount,
                // Locations
                locations: locationVisits,
                // Stagnation
                fem_last_gain_day: gv('fem_last_gain_day'), sub_last_gain_day: gv('sub_last_gain_day'),
                // Anomalies (bracket-bug telemetry — counts choice-only tags fired as narrative tags)
                bracket_bugs: (() => {
                    try {
                        const arr = window.__bracketBugs || [];
                        if (!arr.length) return null;
                        const byKey = {};
                        for (const b of arr) byKey[b.key] = (byKey[b.key] || 0) + 1;
                        const sample = arr.slice(-3).map(b => ({ k: b.key, knot: b.knot }));
                        return { count: arr.length, by_key: byKey, sample };
                    } catch(e) { return null; }
                })(),
                // State invariant violations (master leaks, equipment desync, stat overflow).
                // Emitted by assert_state_consistent() in overnight_processing. One entry
                // per unique violation key per session — duplicates dedup'd at emit time.
                state_violations: (() => {
                    try {
                        const arr = window.__stateViolations || [];
                        if (!arr.length) return null;
                        return { count: arr.length, keys: arr.map(v => v.key), sample: arr.slice(-3) };
                    } catch(e) { return null; }
                })(),
                // Asset-loading diagnostics: tar integrity + missing image
                // names. Read via window (import-cycle-free, same pattern as
                // the two blocks above). Aggregate tar.complete=false counts
                // surface CDN/truncation incidents within hours instead of
                // waiting on player bug reports (VPS reports 2026-06-09).
                assets: (() => {
                    try {
                        const tar = window.__tarStatus ? window.__tarStatus() : null;
                        const missing = window.__imageMissNames ? window.__imageMissNames() : [];
                        if (!tar && !missing.length) return null;
                        return { tar, missing: missing.slice(0, 20), missing_count: missing.length };
                    } catch(e) { return null; }
                })()
            }
        });
    }

    function trySend() {
        if (isAnalyticsOptedOut()) return;
        if (isSending || !storyReady) return;
        const elapsed = (Date.now() - sessionStart) / 1000;
        if (elapsed < MIN_SESSION_SEC) return;
        const vpsPayload = _buildVpsPayload();
        if (!vpsPayload) return;
        isSending = true;
        fetch(VPS_URL, { method: 'POST', headers: {'Content-Type': 'application/json'}, body: vpsPayload })
            .then(() => { isSending = false; })
            .catch(() => { isSending = false; });
    }

    function sendBeacon() {
        if (isAnalyticsOptedOut()) return;
        if (!storyReady || sentFinal) return;
        sentFinal = true;
        const elapsed = (Date.now() - sessionStart) / 1000;
        if (elapsed < MIN_SESSION_SEC) return;
        const vpsPayload = _buildVpsPayload();
        if (!vpsPayload) return;
        try { navigator.sendBeacon(VPS_URL, new Blob([vpsPayload], {type: 'application/json'})); } catch(e) {}
    }

    function gv(name) {
        // Live Ink state first (covers non-observed vars like hypno_exposure, dating_*, office_stage).
        // Fallback to baseVar for the small window before _story is bound.
        try {
            const inkV = variableManager?.getInkVariable?.(name);
            if (inkV !== undefined && inkV !== null) return inkV;
            return variableManager?.getValue?.(name) ?? 0;
        } catch(e) { return 0; }
    }
    function gb(name) { const v = gv(name); return v === true || v === 1; }
    function gs(name) {
        // Mirror gv() but for string vars (trait_1/trait_2 etc): try live Ink state first.
        // Pre-fix gs() only read baseVar, but string-typed vars like trait_1 are written
        // directly to story.variablesState by character-editor.js and never land in baseVar.
        // Symptom: 18,541 telemetry snapshots all reported trait_1='' / trait_2=''.
        try {
            const inkV = variableManager?.getInkVariable?.(name);
            if (inkV !== undefined && inkV !== null) return inkV;
            return variableManager?.getValue?.(name) ?? '';
        } catch(e) { return ''; }
    }

    return { init, trySend, recordLocation: (loc) => { locationVisits[loc] = (locationVisits[loc]||0) + 1; } };
})();

// ============================================
// Save Health Telemetry (anonymous)
// ============================================
// Strategy:
// - session_start fires ONCE per page load with save_count
//   This is the primary metric: aggregate "save presence rate" over time
//   reveals storage wipes (Safari ITP, quota eviction) without per-user tracking
// - save_loss / save_failed / save_fallback_failed are deduped per session
//   They fire on rare specific events; aggregate trends are more reliable
export const saveHealthService = (() => {
    const VPS_URL = 'https://www.pinkedandowned.com/api/analytics/save-health';
    const _sentThisSession = new Set();

    function _buildBase() {
        // Daily-rotating session id shared with the session service — both
        // endpoints use the same day-granularity anonymous id so the backend
        // can dedupe save-health events against concurrent session events.
        const ver = _versionInfo().fullVersion;
        const platform = window.Capacitor ? 'android' : (window.electronAPI ? 'electron' : 'web');
        return { session_id: dailySessionId(), version: ver, platform };
    }

    function _post(payload) {
        if (isAnalyticsOptedOut()) return;
        try {
            const body = JSON.stringify(payload);
            if (navigator.sendBeacon) {
                const blob = new Blob([body], { type: 'application/json' });
                navigator.sendBeacon(VPS_URL, blob);
            } else {
                fetch(VPS_URL, { method: 'POST', headers: {'Content-Type': 'application/json'}, body }).catch(() => {});
            }
        } catch(e) { /* never crash on telemetry */ }
    }

    function _sendDeduped(eventType, extra = {}) {
        if (isAnalyticsOptedOut()) return;
        if (_sentThisSession.has(eventType)) return;
        _sentThisSession.add(eventType);
        _post({ event_type: eventType, ..._buildBase(), ...extra });
    }

    /**
     * Send session_start event with save_count.
     * NOT deduped (this is the metric we want to track per session).
     * Called once after saveSystem.init() completes.
     */
    function sendSessionStart(saveCount) {
        if (isAnalyticsOptedOut()) return;
        _post({
            event_type: 'session_start',
            ..._buildBase(),
            save_count: saveCount,
        });
    }

    function init() {
        if (isAnalyticsOptedOut()) return;
        // Save loss detected at init (rare, only when SOME storage survived but saves are gone)
        events.on(Events.SAVE_LOSS_WARNING, (data) => {
            let daysSince = null;
            if (data?.lastPlayed) {
                try {
                    const last = new Date(data.lastPlayed);
                    daysSince = Math.floor((Date.now() - last.getTime()) / 86400000);
                } catch(e) {}
            }
            _sendDeduped('save_loss', { days_since_last: daysSince });
        });

        // Manual save failed (rare, user-initiated)
        events.on(Events.SAVE_FAILED, (data) => {
            _sendDeduped('save_failed', { slot: data?.slot, reason: (data?.reason || '').slice(0, 500) });
        });

        // localStorage fallback failed — could fire on EVERY autosave if quota is full
        // Dedupe ensures we only learn "user X has quota issues" once per session
        events.on(Events.SAVE_FALLBACK_FAILED, (data) => {
            _sendDeduped('save_fallback_failed', { slot: data?.slot, reason: (data?.reason || '').slice(0, 500) });
        });

        // Export-save diagnostic — fires once per Export Save click.
        // NOT deduped: a player exporting 5 times with 4× Day 1 and 1× Day N is
        // exactly the diagnostic signal we need. Volume is low (manual user
        // action), so unbounded count is fine.
        events.on(Events.SAVE_EXPORT_DIAGNOSTIC, (data) => {
            if (isAnalyticsOptedOut()) return;
            _post({
                event_type: 'export_diagnostic',
                ..._buildBase(),
                day: data?.day,
                live_day: data?.liveDay,
                has_ink_state: data?.hasInkState,
                ink_state_len: data?.inkStateLen,
                ls_ts: data?.lsTs,
                idb_ts: data?.idbTs,
                drift: data?.drift,
                is_auto: data?.isAuto,
                slot: data?.slot,
                save_version: data?.saveVersion,
                game_version_save: data?.gameVersion,
            });
        });
    }

    return { init, sendSessionStart };
})();
